import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {DisplayContext as PropDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {ReportName} from 'common/report/constants';
import {
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  FUNNELS_TIME_TO_CONVERSION,
} from 'common/query-builder/query-block/properties-menu/constants';
import {MenuItemType, SelectPropertyEvent} from 'common/query-builder/query-block/properties-menu/types';
import {Segmentation, WindowLengthUnit, WindowType} from 'common/types/reports/query';
import {SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {propertySelectionToSegmentation} from 'common/query-builder/query-block/segment-menu/util';

import {
  DEFAULT_AGGREGATION_BEHAVIOR,
  DEFAULT_BEHAVIORLESS_SEGMENTATION,
  DEFAULT_FREQUENCY_BEHAVIOR,
  DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
  getDefaultPropertyValueForBehavior,
  isMultiAttributionSegmentation,
} from '../row-types/behavior/utils';

/**
 * Populates the right `Segmentation` format based on items selected from `mp-properties-menu`
 */
export function getPropertySelectionFromEvent(
  ev: SelectPropertyEvent,
  currentSelection: Segmentation,
  {isForFilter, reportName}: {isForFilter?: boolean; reportName?: ReportName} = {},
): SegmentValue | Partial<PropertyFilter> {
  const {menuItemType, item, stagedItemsStack} = ev.detail;
  const isMultiAttributionBehavior = stagedItemsStack?.some(
    (item) => item.menuItemType === MenuItemType.BehaviorMultiAttribution,
  );
  let selection: SegmentValue | Partial<PropertyFilter>;
  const isTimeToConvertSegmentation = item[`value`] === FUNNELS_TIME_TO_CONVERSION.value;
  const isTimeToConvertFilter = item[`propertyName`] === FUNNELS_TIME_TO_CONVERSION.value;

  if (!stagedItemsStack?.length && menuItemType === MenuItemType.BehaviorWithEvent) {
    const isBehavior =
      [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value].includes(item[`value`]) ||
      [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value].includes(item[`propertyName`]);
    const isAggregation =
      item[`value`] === BEHAVIOR_AGGREGATION.value || item[`propertyName`] === BEHAVIOR_AGGREGATION.value;
    const shouldSetStepNumbers = isBehavior && reportName === ReportName.Funnels;
    const shouldSetWindowDuration = isBehavior && reportName === ReportName.Retention;
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      behavior: {
        ...(isAggregation ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR),
        ...(shouldSetStepNumbers
          ? {
              startStepNumber: 0,
              endStepNumber: 1,
            }
          : {}),
        ...(shouldSetWindowDuration
          ? {
              windowType: WindowType.AFTER,
              windowLength: 7,
              windowLengthUnit: WindowLengthUnit.DAY,
            }
          : {}),
        event: {custom: false, label: ``, value: null},
        property: getDefaultPropertyValueForBehavior(isAggregation),
      },
    };
  } else if (isMultiAttributionBehavior) {
    selection = {
      ...propertySelectionToSegmentation(item),
      behavior: {
        ...(isMultiAttributionSegmentation(currentSelection)
          ? // keep any existing behavior selection
            currentSelection.behavior
          : DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR),
        property: item as PropertySectionItem,
      },
    };
  } else if (isForFilter) {
    selection = PropertyFilterFactory.createPropertyFilter(item as Partial<PropertyFilter>, {
      displayContext: PropDisplayContext.Default,
    });
    if (isTimeToConvertFilter) {
      selection = {
        ...selection,
        startStepNumber: null, // Start selection with "All Steps"
        endStepNumber: null,
      };
    }
  } else if (isTimeToConvertSegmentation) {
    selection = {
      ...propertySelectionToSegmentation(item),
      startStepNumber: null, // Start selection with "All Steps"
      endStepNumber: null,
    };
  } else {
    selection = propertySelectionToSegmentation(item);
  }

  return selection;
}
